import * as React from "react";
import AlertTriangleIcon from "../../../../components/Icons/AlertTriangle";
import ListState from "../../../../components/ListState/ListState";
import Skeleton from "../../../../components/Skeleton/Skeleton";
import {
    getROILink,
    getSessionsNeedingConsent,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import { useFetchUserPractices } from "../../../auth/hooks/auth.queries";
import {
    IPracticeActiveSession,
    IPracticeBaseSession,
    IPracticeHeldSession,
} from "../../../auth/types/auth.types";
import {
    useGetClientConsents,
    useGetClientForms,
} from "../../hooks/client-forms.queries";
import ConsentForms from "../ConsentForms/ConsentForms";
import OtherForms from "../OtherForms/OtherForms";
import useCurrentClientProfile from "../../../../hooks/useCurrentClient";
import { PORTAL_SUPPORTED_FORM_TYPES } from "../../types/client-forms.types";

export default function ClientFormsWrapper() {
    const currentProfile = useCurrentClientProfile();
    const { data: userPractices } = useFetchUserPractices(
        currentProfile?.userId ?? ""
    );

    const {
        data: clientsConsents,
        isLoading: consentsLoading,
        error: consentsError,
    } = useGetClientConsents({});

    const {
        data: clientForms,
        isLoading: formsLoading,
        error: formsError,
    } = useGetClientForms({});

    const selectedPractice = userPractices?.data?.practices.find(
        (practice) =>
            practice.practice_id === currentProfile?.practice.practice_id
    );

    const practiceName = currentProfile?.practice?.practice_name;
    const roiLink = practiceName ? getROILink(practiceName) : "";

    const sessionsNeedingConsent = getSessionsNeedingConsent([
        ...(selectedPractice?.active_sessions ?? []),
        ...(selectedPractice?.held_sessions ?? []),
    ]) as (IPracticeActiveSession | IPracticeHeldSession)[];

    const isLoading = consentsLoading || formsLoading;
    const hasError = !isLoading && (!!consentsError || !!formsError);
    const isEmpty =
        !isLoading &&
        !hasError &&
        clientsConsents?.data?.length === 0 &&
        clientForms?.data?.length === 0;

    const consentFormUrl = (session: IPracticeBaseSession) =>
        session.consent?.consent_form_type &&
        PORTAL_SUPPORTED_FORM_TYPES.has(session.consent.consent_form_type) &&
        session.booking_id
            ? `/forms/consent/${session.booking_id}`
            : session.consent?.consent_form_link;

    return (
        <div>
            <div className="page">
                {sessionsNeedingConsent.length > 0 && (
                    <div className="mb-16 rounded-r8 border border-warning-stroke bg-warning-surface p-12">
                        <div className="flex items-center gap-8">
                            <AlertTriangleIcon stroke="var(--color-warning-text)" />
                            <p className="flex gap-2 text-sm font-semibold text-warning-strong">
                                <span>
                                    {`${sessionsNeedingConsent.length} session`}

                                    {sessionsNeedingConsent.length !== 1
                                        ? "s"
                                        : ""}
                                </span>
                                require a consent form
                            </p>
                        </div>
                        <p className="mt-2 text-xs text-warning-strong">
                            Please fill out the consent form for each session
                            listed below. Submitted consent forms are reviewed
                            before they are marked completed.
                        </p>
                        <ul className="mt-10 space-y-6">
                            {sessionsNeedingConsent.map((session) => (
                                <li
                                    key={
                                        "session_id" in session
                                            ? session.session_id
                                            : session.held_session_id
                                    }
                                    className="flex items-center flex-wrap justify-between gap-8 rounded-r8 border border-warning-stroke/50 bg-white px-12 py-8"
                                >
                                    <div>
                                        <p className="flex flex-wrap gap-1 text-sm font-medium text-black">
                                            <span>
                                                {makeStringFirstLetterCapital(
                                                    removeEnumUnderscore(
                                                        session.service
                                                    )
                                                )}
                                            </span>
                                            •
                                            <span className="">
                                                {session.provider.first_name}
                                            </span>
                                            {session.provider.last_name}
                                            {session.provider.credential
                                                ? ` • ${session.provider.credential}`
                                                : ""}
                                        </p>
                                        <p className="text-xs text-muted capitalize">
                                            {removeEnumUnderscore(
                                                session.delivery_option
                                            )}
                                        </p>
                                    </div>
                                    <a
                                        href={consentFormUrl(session)}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="shrink-0 text-xs font-semibold text-primary underline underline-offset-2"
                                    >
                                        Fill out form
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="mb-16 rounded-r8 border border-stroke-subtle bg-surface-raised p-12">
                    <p className="text-sm font-semibold text-black">
                        Need to release records or authorize information
                        sharing?
                    </p>

                    <p className="text-xs text-muted">
                        Complete the Release of Information form and submit it
                        for review.
                        {roiLink && (
                            <a
                                href={roiLink}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-4 text-xs font-semibold text-primary underline underline-offset-2"
                            >
                                Open ROI form
                            </a>
                        )}
                    </p>
                </div>

                {isLoading && (
                    <Skeleton
                        width="100%"
                        height={400}
                        className="rounded-r8"
                        containerTestId="forms-loader"
                    />
                )}

                {hasError && (
                    <ListState
                        isError
                        errorMsg="Could not load your forms. Please try again."
                        stateHelperText="Try reloading this page to solve this issue"
                    />
                )}

                {isEmpty && (
                    <ListState
                        isError={false}
                        emptyMessage="No forms available"
                        stateHelperText="Your forms will appear here once they are added"
                    />
                )}

                {!isLoading && !hasError && !isEmpty && (
                    <div className="flex flex-col gap-16">
                        {clientsConsents?.data &&
                            clientsConsents?.data.length > 0 && (
                                <ConsentForms />
                            )}
                        {clientForms?.data && clientForms.data.length > 0 && (
                            <OtherForms />
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
